import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

function GuestView() {
  const { logout } = useAuth();
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBlueMap, setShowBlueMap] = useState(null); // server object or null

  // Player follow state
  const [followPlayer, setFollowPlayer] = useState(null);
  const [playerPositions, setPlayerPositions] = useState({});
  const bluemapIframeRef = useRef(null);
  const positionPollRef = useRef(null);

  useEffect(() => {
    loadServers();
    const interval = setInterval(loadServers, 10000);
    return () => clearInterval(interval);
  }, []);

  async function loadServers() {
    try {
      const data = await api.getServers();
      setServers(data);
      // If the BlueMap server was removed, close viewer
      if (showBlueMap) {
        const current = data.find(s => s.id === showBlueMap.id);
        if (!current) {
          setShowBlueMap(null);
          setFollowPlayer(null);
        }
      }
    } catch (e) {
      console.error('Failed to load servers:', e);
    } finally {
      setLoading(false);
    }
  }

  // BlueMap player position polling
  const pollPlayerPositions = useCallback(async (server) => {
    if (!server || !server.bluemapPort) return;
    try {
      const res = await fetch(`/api/bluemap/${server.id}/maps/world/live/players.json`);
      if (res.ok) {
        const data = await res.json();
        const positions = {};
        if (data.players) {
          for (const p of data.players) {
            if (p.name && p.position) {
              positions[p.name] = { x: p.position.x, y: p.position.y, z: p.position.z };
            }
          }
        }
        setPlayerPositions(positions);
      }
    } catch (e) {
      // BlueMap not reachable, ignore
    }
  }, []);

  // Start/stop position polling when BlueMap is open
  useEffect(() => {
    if (showBlueMap && showBlueMap.bluemapPort) {
      pollPlayerPositions(showBlueMap);
      positionPollRef.current = setInterval(() => pollPlayerPositions(showBlueMap), 5000);
    }
    return () => {
      if (positionPollRef.current) {
        clearInterval(positionPollRef.current);
        positionPollRef.current = null;
      }
    };
  }, [showBlueMap, pollPlayerPositions]);

  // Update iframe hash when following a player
  useEffect(() => {
    if (followPlayer && playerPositions[followPlayer] && bluemapIframeRef.current) {
      const pos = playerPositions[followPlayer];
      const hash = `#world:${Math.round(pos.x)}:${Math.round(pos.z)}:${Math.round(pos.y)}:100:0.6:0:0:0:perspective`;
      try {
        bluemapIframeRef.current.contentWindow.location.hash = hash;
      } catch (e) {
        // Cross-origin fallback
        const baseUrl = getBluemapUrl(showBlueMap);
        if (baseUrl) {
          bluemapIframeRef.current.src = baseUrl + hash;
        }
      }
    }
  }, [followPlayer, playerPositions, showBlueMap]);

  const runningServers = servers.filter(s => s.status === 'running');
  const totalPlayers = servers.reduce((sum, s) => sum + (s.players?.length || 0), 0);

  function getBluemapUrl(server) {
    if (!server || !server.bluemapPort) return null;
    return `/api/bluemap/${server.id}/`;
  }

  function handleOpenBlueMap(server) {
    setShowBlueMap(server);
    setFollowPlayer(null);
    setPlayerPositions({});
  }

  function handleCloseBlueMap() {
    setShowBlueMap(null);
    setFollowPlayer(null);
    setPlayerPositions({});
  }

  function handleFollowPlayer(playerName) {
    if (followPlayer === playerName) {
      setFollowPlayer(null);
    } else {
      setFollowPlayer(playerName);
    }
  }

  if (loading) {
    return (
      <div className="guest-layout">
        <div className="empty-state"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="guest-layout">
      <header className="guest-header">
        <div className="flex items-center gap-3">
          <h1 style={{ fontSize: 26, color: '#fcb41c', textShadow: '2px 2px 0 rgba(0,0,0,0.6)' }}>LexWar's MC Versteck</h1>
        </div>
        <div className="flex items-center gap-3">
          <span style={{ color: '#d4c483' }}>Zuschauer-Modus</span>
          <button className="btn btn-secondary btn-sm" onClick={logout}>Abmelden</button>
        </div>
      </header>

      <main className="guest-content">
        {showBlueMap && showBlueMap.bluemapPort ? (
          <div className="bluemap-viewer-container">
            <div className="bluemap-viewer-header">
              <button className="btn btn-secondary btn-sm" onClick={handleCloseBlueMap}>
                &lt; Zurueck
              </button>
              <h2>{showBlueMap.name} - Weltkarte</h2>
              <span className={`status-badge ${showBlueMap.status}`}>
                <span className="status-dot" />
                Online
              </span>
            </div>

            {/* Player Follow Controls */}
            {showBlueMap.players && showBlueMap.players.length > 0 && (
              <div className="follow-controls">
                <span className="follow-label">Spieler folgen:</span>
                <div className="follow-player-list">
                  {showBlueMap.players.map(name => (
                    <button
                      key={name}
                      className={`follow-player-btn ${followPlayer === name ? 'active' : ''}`}
                      onClick={() => handleFollowPlayer(name)}
                    >
                      {name}
                      {playerPositions[name] && (
                        <span className="follow-coords">
                          ({Math.round(playerPositions[name].x)}, {Math.round(playerPositions[name].z)})
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {followPlayer && (
                  <button className="btn btn-secondary btn-sm" onClick={() => setFollowPlayer(null)}>
                    Folgen beenden
                  </button>
                )}
              </div>
            )}

            <div className="bluemap-viewer-frame">
              <iframe
                ref={bluemapIframeRef}
                src={getBluemapUrl(showBlueMap)}
                title={`BlueMap - ${showBlueMap.name}`}
                style={{ width: '100%', height: '100%', border: 'none' }}
                allowFullScreen
              />
            </div>
          </div>
        ) : (
          <>
            <div className="page-header">
              <h2>Server-Uebersicht</h2>
            </div>

            <div className="grid grid-3">
              <div className="stat-card">
                <div className="stat-icon">&#127981;</div>
                <div className="stat-value">{servers.length}</div>
                <div className="stat-label">Server gesamt</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">&#9989;</div>
                <div className="stat-value">{runningServers.length}</div>
                <div className="stat-label">Server online</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">&#128100;</div>
                <div className="stat-value">{totalPlayers}</div>
                <div className="stat-label">Spieler online</div>
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              {servers.length === 0 ? (
                <div className="empty-state">
                  <div className="icon">&#127981;</div>
                  <p>Noch keine Server konfiguriert</p>
                </div>
              ) : (
                <div className="guest-server-list">
                  {servers.map(server => {
                    const isRunning = server.status === 'running';
                    const playerCount = server.players?.length || 0;
                    const hasBluemap = server.bluemapPort && isRunning;
                    return (
                      <div key={server.id} className="guest-server-item">
                        <div className="server-card">
                          <div className="flex items-center justify-between">
                            <span className="server-name">{server.name}</span>
                            <div className="flex items-center gap-2">
                              {isRunning && playerCount > 0 && (
                                <span className="player-count-badge">{playerCount} Spieler</span>
                              )}
                              <span className={`status-badge ${server.status}`}>
                                <span className="status-dot" />
                                {isRunning ? 'Online' : server.status === 'stopped' ? 'Offline' : server.status}
                              </span>
                            </div>
                          </div>
                          <div className="server-meta">
                            <span>Spieler: {playerCount}</span>
                            {server.uptime > 0 && (
                              <span>Uptime: {formatUptime(server.uptime)}</span>
                            )}
                          </div>
                          {isRunning && server.players && server.players.length > 0 && (
                            <div className="server-player-names">
                              {server.players.map(name => (
                                <span key={name} className="tag">{name}</span>
                              ))}
                            </div>
                          )}
                          {hasBluemap && (
                            <button
                              className="btn btn-primary btn-sm"
                              style={{ marginTop: 12 }}
                              onClick={() => handleOpenBlueMap(server)}
                            >
                              &#128506; Weltkarte oeffnen
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function formatUptime(ms) {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

export default GuestView;
